import { Swiper } from 'swiper/types';
import { GraphQLID } from '@/@types/shopify';
import { debounce } from '@/entrypoints/utils';
import { createLogger } from '@/lib/logger';
import { Cart, CartEvents, CartLine, TypedCartEvent } from '../internals/core';
import { CartEventBus } from '../internals/event-bus';
import { MiniCart } from './mini-cart';

export type PreviewGift = {
  id: GraphQLID;
  product_title: string;
  variant_title: string;
  featured_image: string;
  sku: string;
  price: string;
  compare_at_price: string;
  quantity: number;
};

export type PreviewResponse =
  | {
      status: 'success';
      gifts: PreviewGift[];
      totalGiftValue: number;
      totalGiftQuantity: number;
    }
  | {
      status: 'error';
      message: string;
    };

export class MiniCartGiftsAccordion extends HTMLElement {
  private abortController: AbortController | null = null;
  private eventBus: CartEventBus | null = null;
  private logger = createLogger('mini-cart-gifts-accordion');
  private headerLabelTemplate: string | null =
    'See the {gift_count} gift{plural} added in your order';
  private headerElem: HTMLElement | null = null;
  private carouselRootElem: HTMLElement | null = null;
  private apiUrl: string | null = null;
  private parentMiniCart: MiniCart | null = null;
  private swiper: Swiper | null = null;

  private additionalGifts: PreviewGift[] = [];

  private slideTemplate: HTMLTemplateElement | null = null;

  private debouncedCartChange = debounce(this.onCartChanged.bind(this), 300);

  constructor() {
    super();
  }

  // Add internal logic here as separated functions
  private onCartChanged(e: TypedCartEvent<'cart:changed:debounced'>) {
    this.logger.debug('onCartChanged', e);
    const { cart } = e.detail;
    this.render(cart);
    this.updateSwiper();
  }

  private getCookie(name: string) {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) return parts.pop()!.split(';').shift();
  }

  private hideAccordion() {
    this.classList.add('hidden');
  }

  private showAccordion() {
    this.classList.remove('hidden');
  }

  private generateSlides(gifts: PreviewGift[]): string {
    const mergedGifts = [...gifts, ...this.additionalGifts];
    return mergedGifts
      .map((gift) => {
        return this.slideTemplate!.innerHTML.replace(/{image}/g, gift.featured_image)
          .replace(/{alt}/g, gift.product_title)
          .replace(/{title}/g, gift.product_title)
          .replace(/{quantity}/g, gift.quantity > 1 ? `${gift.quantity}x` : '')
          .replace(/{price}/g, gift.compare_at_price);
      })
      .join('');
  }

  addAdditionalGifts(gifts: PreviewGift[]) {
    gifts.forEach((gift) => {
      if (this.additionalGifts.find((g) => g.id === gift.id)) {
        return;
      }
      this.additionalGifts.push(gift);
    });
    this.render(this.parentMiniCart?.store.getCart()!);
    this.updateSwiper();
  }

  setAdditionalGifts(gifts: PreviewGift[]) {
    this.additionalGifts = gifts;
    this.render(this.parentMiniCart?.store.getCart()!);
    this.updateSwiper();
  }

  updateAdditionalGifts(gifts: PreviewGift[]) {
    const giftsToRemove = gifts.filter((gift) => gift.quantity === 0);

    if (giftsToRemove.length > 0) {
      const idsToRemove = new Set(giftsToRemove.map((g) => g.id));
      this.additionalGifts = this.additionalGifts.filter((g) => !idsToRemove.has(g.id));
    }

    gifts.forEach((gift) => {
      if (gift.quantity === 0) return;

      const existingIndex = this.additionalGifts.findIndex((g) => g.id === gift.id);
      if (existingIndex !== -1) {
        this.additionalGifts[existingIndex] = gift;
      } else {
        this.additionalGifts.push(gift);
      }
    });

    this.render(this.parentMiniCart?.store.getCart()!);
    this.updateSwiper();
  }

  private initSwiper() {
    if (!this.carouselRootElem) return;

    this.swiper = new window.Swiper(this.carouselRootElem, {
      slidesPerView: 'auto',
      slidesPerGroup: 2,
      slidesOffsetAfter: 16,
      slidesOffsetBefore: 16,
      spaceBetween: 16,
      centerInsufficientSlides: true,
      scrollbar: {
        el: '.swiper-scrollbar',
        draggable: true,
      },
      on: {
        resize(swiper) {
          swiper.update();
        },
      },
    });
  }

  private updateSwiper() {
    if (!this.swiper) {
      this.initSwiper();
      return;
    }

    this.swiper.update();
  }

  private render(cart: Cart) {
    try {
      if (!cart) {
        this.hideAccordion();
        return;
      }

      if (cart.item_count === 0) {
        this.hideAccordion();
        return;
      }

      const cartToken = this.getCookie('cart');

      if (!cartToken) {
        this.hideAccordion();
        return;
      }

      // The gifts API authors its price thresholds in the shop currency, but the
      // cart it reads back from the Storefront API is priced in the buyer's
      // presentment currency. Shopify exposes no exchange rate through either
      // API, so the rate has to come from the storefront.
      const currencyRate = parseFloat(window.Shopify.currency.rate || '1');

      fetch(this.apiUrl!, {
        method: 'POST',
        body: JSON.stringify({
          cartToken: decodeURIComponent(cartToken),
          currencyRate,
        }),
        signal: this.abortController!.signal,
      })
        .then((r) => r.json())
        .then((data: PreviewResponse) => {
          if (data.status === 'error') {
            this.logger.error(data.message);
            return;
          }

          const newHeading = this.headerLabelTemplate!.replace(
            /{gift_count}/g,
            (data.totalGiftQuantity + this.additionalGifts.length).toString(),
          ).replace(
            /{plural}/g,
            data.totalGiftQuantity + this.additionalGifts.length > 1 ? 's' : '',
          );

          const labelElem = this.headerElem!.querySelector<HTMLSpanElement>(
            '.mini-cart__accordion-toggle-label',
          );

          if (!labelElem) {
            console.error('No label element found!');
            return;
          }

          labelElem.textContent = newHeading;

          if (data.gifts.length === 0 && !this.additionalGifts.length) {
            this.hideAccordion();
            return;
          }

          const slidesHtml = this.generateSlides(data.gifts);

          const swiperWrapper = this.carouselRootElem?.querySelector('.swiper-wrapper');

          if (!swiperWrapper) {
            this.hideAccordion();
            return;
          }

          swiperWrapper.innerHTML = slidesHtml;

          this.showAccordion();
        });
    } catch (error) {
      this.logger.error(error);
    }
  }

  private bindEvents() {
    this.eventBus!.on(CartEvents.DebouncedChange, this.debouncedCartChange);

    this.eventBus!.on(CartEvents.UIOpened, () => {
      this.render(this.parentMiniCart?.store.getCart()!);
      this.updateSwiper();
    });

    this.headerElem!.addEventListener('click', () => {
      this.eventBus!.emit(CartEvents.UIUpdate, { action: 'toggle-gifts-accordion' });

      this.setAttribute(
        'aria-expanded',
        this.getAttribute('aria-expanded') === 'true' ? 'false' : 'true',
      );
    });
  }

  private unbindEvents() {
    this.eventBus!.off(CartEvents.DebouncedChange, this.debouncedCartChange);
  }

  private onMiniCartReady(parentMiniCart: MiniCart) {
    // Make sure we use the same event bus as the parent mini-cart.
    this.eventBus = parentMiniCart.bus;

    this.bindEvents();
  }

  // Lifecycle hooks
  connectedCallback() {
    this.abortController = new AbortController();

    const parentMiniCart = this.closest('mini-cart')! as MiniCart;
    if (!parentMiniCart) {
      this.logger.error('MiniCartGiftsAccordion must be a child of a MiniCart element');
      throw new Error('MiniCartGiftsAccordion must be a child of a MiniCart element');
    }

    this.parentMiniCart = parentMiniCart;

    this.slideTemplate = this.querySelector(
      '#gifts-accordion-template',
    ) as HTMLTemplateElement | null;

    if (!this.slideTemplate) {
      this.logger.error('MiniCartGiftsAccordion must have a #gifts-accordion-template element');
      throw new Error('MiniCartGiftsAccordion must have a #gifts-accordion-template element');
    }

    this.headerElem = this.querySelector('[data-gifts-accordion-toggle]') as HTMLElement | null;

    if (!this.headerElem) {
      this.logger.error('MiniCartGiftsAccordion must have a [data-gifts-accordion-toggle] element');
      throw new Error('MiniCartGiftsAccordion must have a [data-gifts-accordion-toggle] element');
    }

    this.carouselRootElem = this.querySelector('[data-gifts-list]') as HTMLElement | null;

    if (!this.carouselRootElem) {
      this.logger.error('MiniCartGiftsAccordion must have a [data-gifts-list] element');
      throw new Error('MiniCartGiftsAccordion must have a [data-gifts-list] element');
    }

    this.apiUrl = this.getAttribute('data-api-url');

    if (!this.apiUrl) {
      this.logger.error('MiniCartGiftsAccordion must have a data-api-url attribute');
      throw new Error('MiniCartGiftsAccordion must have a data-api-url attribute');
    }

    parentMiniCart.execOnReady(this.onMiniCartReady.bind(this, parentMiniCart));
  }

  disconnectedCallback() {
    this.abortController?.abort();

    this.unbindEvents();
  }
}

if (!customElements.get('mini-cart-gifts-accordion')) {
  customElements.define('mini-cart-gifts-accordion', MiniCartGiftsAccordion);
}
